// SafetyFormsApi.js
// Facilitating API calls from the

import axios from "axios";
import { getCsrfToken, setupCsrfInterceptor } from "../auth/csrfUtils";

const api = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  // Session cookies are required for this API
  withCredentials: true,
});

// Setup CSRF protection for this API instance
setupCsrfInterceptor(api);

const baseSafetyFormUrl = "/safety_forms";

// Pulling a users near miss form lists
export const getUserNearMissFormList = async (
  employeeId,
  page = 1,
  per_page = 10,
  filters = null,
  globalSearch = false
) => {
  const nearMissFilters = {
    created_by: globalSearch ? filters?.createdBy : employeeId,
    status: filters?.status,
    incident_date_start: filters?.incidentDateStart,
    incident_date_end: filters?.incidentDateEnd,
    incident_time_start: filters?.incidentTimeStart,
    incident_time_end: filters?.incidentTimeEnd,
    reported_to: filters?.reportedTo,
    reported_by: filters?.reportedBy,
    created_date_start: filters?.createdStartDate,
    created_date_end: filters?.createdEndDate,
  };
  const response = await api.post(
    `${baseSafetyFormUrl}/user_near_miss_form/list/`,
    {
      employee_id: globalSearch ? null : employeeId,
      page: page,
      per_page: per_page,
      filters: nearMissFilters,
      global_search: globalSearch,
    }
  );
  return response.data;
};

// Pulling a users safety audit checklist form lists
export const getUserSafetyAuditChecklistFormList = async (
  employeeId,
  page = 1,
  per_page = 10,
  filters = null,
  globalSearch = false
) => {
  const safetyAuditChecklistFilters = {
    status: filters?.status,
    created_by: globalSearch ? filters?.createdBy : employeeId,
    audit_date_start: filters?.auditDateStart,
    audit_date_end: filters?.auditDateEnd,
    audit_time_start: filters?.auditTimeStart,
    audit_time_end: filters?.auditTimeEnd,
    project_number: filters?.projectNumber,
    supervisor: filters?.supervisor,
    created_date_start: filters?.createdStartDate,
    created_date_end: filters?.createdEndDate,
  };
  const response = await api.post(
    `${baseSafetyFormUrl}/user_safety_audit_checklist_form/list/`,
    {
      employee_id: globalSearch ? null : employeeId,
      page: page,
      per_page: per_page,
      filters: safetyAuditChecklistFilters,
      global_search: globalSearch,
    }
  );
  return response.data;
};

// Pulling a users incident investigation form lists
export const getUserIncidentInvestigationFormList = async (
  employeeId,
  page = 1,
  per_page = 10,
  filters = null
) => {
  const incidentInvestigationFilters = {
    created_by: filters.createdBy,
    status: filters.status,
    incident_investigation_date_start: filters.incidentInvestigationStartDate,
    incident_investigation_date_end: filters.incidentInvestigationEndDate,
    incident_investigation_time_start: filters.incidentInvestigationStartTime,
    incident_investigation_time_end: filters.incidentInvestigationEndTime,
  };
  const response = await api.post(
    `${baseSafetyFormUrl}/user_incident_investigation_form/list/`,
    {
      employee_id: employeeId,
      page: page,
      per_page: per_page,
      filters: incidentInvestigationFilters,
    }
  );
  return response.data;
};

// Pulling a single near miss form information
export const getNearMissFormInfo = async (nearMissId) => {
  const response = await api.get(
    `${baseSafetyFormUrl}/near_miss_form/info/${nearMissId}`
  );
  return response.data;
};

// Pulling a single safety audit checklist form information
export const getSafetyAuditChecklistFormInfo = async (
  safetyAuditChecklistId
) => {
  const response = await api.get(
    `${baseSafetyFormUrl}/safety_audit_checklist_form/info/${safetyAuditChecklistId}`
  );
  return response.data;
};

// Pulling a single incident investigation form information
export const getIncidentInvestigationFormInfo = async (
  incidentInvestigationId
) => {
  const response = await api.get(
    `${baseSafetyFormUrl}/incident_investigation_form/info/${incidentInvestigationId}`
  );
  return response.data;
};

// Near Miss Form API calls

// Creating a draft of a nearMissForm
export const createNearMissFormDraft = async (userContext) => {
  const response = await api.post(
    `${baseSafetyFormUrl}/create_near_miss_form_draft`,
    {
      user_id: userContext.employee_id,
    },
    {
      headers: {
        "X-CSRF-TOKEN": getCsrfToken(),
      },
    }
  );
  if (response.data?.logged_in === false) {
    window.location.href = "/login";
    throw new Error("Session expired, please login again.");
  } else {
    return response.data;
  }
};

// Submitting a nearMissForm (simplified - data already saved via WebSocket autosave)
export const submitNearMissForm = async (payload) => {
  // payload is { near_miss_id: "uuid" }
  const response = await api.post(
    `${baseSafetyFormUrl}/submit_near_miss_form`,
    payload,
    {
      headers: {
        "X-CSRF-TOKEN": getCsrfToken(),
      },
    }
  );
  if (response.data?.logged_in === false) {
    window.location.href = "/login";
    throw new Error("Session expired, please login again.");
  }
  return response.data;
};

// Safety Audit Checklist Form API calls

// Creating a draft of a safetyAuditChecklistForm
export const createSafetyAuditChecklistFormDraft = async (userContext) => {
  const response = await api.post(
    `${baseSafetyFormUrl}/create_safety_audit_checklist_form_draft`,
    {
      user_id: userContext.employee_id,
    },
    {
      headers: {
        "X-CSRF-TOKEN": getCsrfToken(),
      },
    }
  );
  if (response.data?.logged_in === false) {
    window.location.href = "/login";
    throw new Error("Session expired, please login again.");
  } else {
    return response.data;
  }
};

// Submitting a safetyAuditChecklistForm (simplified - data already saved via WebSocket autosave)
export const submitSafetyAuditChecklistForm = async (payload) => {
  // payload is { safety_audit_id: "uuid" }
  const response = await api.post(
    `${baseSafetyFormUrl}/submit_safety_audit_checklist_form`,
    payload,
    {
      headers: {
        "X-CSRF-TOKEN": getCsrfToken(),
      },
    }
  );
  if (response.data?.logged_in === false) {
    window.location.href = "/login";
    throw new Error("Session expired, please login again.");
  }
  return response.data;
};

// Deleting Forms

// Deleting a near miss form
export const deleteNearMissForm = async (nearMissId) => {
  const response = await api.post(
    `${baseSafetyFormUrl}/delete_near_miss?near_miss_id=${nearMissId}`
  );
  if (response.data?.logged_in === false) {
    window.location.href = "/login";
    throw new Error("Session expired, please login again.");
  } else {
    return response.data;
  }
};

// Deleting the safety form audit
export const deleteSafetyAuditForm = async (safetyAuditId) => {
  const response = await api.post(
    `${baseSafetyFormUrl}/delete_safety_audit?safety_audit_id=${safetyAuditId}`
  );
  if (response.data?.logged_in === false) {
    window.location.href = "/login";
    throw new Error("Session expired, please login again.");
  } else {
    return response.data;
  }
};

// Deleting the incident investigation form
export const deleteIncidentInvestigationForm = async (
  incidentInvestigationId
) => {
  const response = await api.post(
    `${baseSafetyFormUrl}/delete_incident_investigation?incident_investigation_id=${incidentInvestigationId}`
  );
  if (response.data?.logged_in === false) {
    window.location.href = "/login";
    throw new Error("Session expired, please login again.");
  } else {
    return response.data;
  }
};

// DEL Association API calls

// Associate a DEL with a Safety Audit Checklist
export const associateDelWithSafetyAudit = async (safetyAuditId, delId) => {
  const response = await api.post(
    `${baseSafetyFormUrl}/associate_del`,
    {
      safety_audit_id: safetyAuditId,
      del_id: delId,
    },
    {
      headers: {
        "X-CSRF-TOKEN": getCsrfToken(),
      },
    }
  );
  return response.data;
};

// Disassociate a DEL from a Safety Audit Checklist
export const disassociateDelFromSafetyAudit = async (safetyAuditId) => {
  const response = await api.post(
    `${baseSafetyFormUrl}/disassociate_del`,
    {
      safety_audit_id: safetyAuditId,
    },
    {
      headers: {
        "X-CSRF-TOKEN": getCsrfToken(),
      },
    }
  );
  return response.data;
};

// Fetch JSA data for a safety audit's associated DEL (lazy load)
export const getSafetyAuditJsa = async (safetyAuditId) => {
  const response = await api.get(
    `${baseSafetyFormUrl}/safety_audit_jsa/${safetyAuditId}`
  );
  return response.data;
};
